import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronsUpDown } from 'lucide-react'
import { memo } from 'react'

interface ResetFiltersMenuProps {
  label: string
  hasCurrentFilters: boolean
  hasUnsavedChanges: boolean
  onResetAll: () => void
  onRevertToSaved: () => void
}

const ResetFiltersMenu = ({
  label,
  hasCurrentFilters,
  hasUnsavedChanges,
  onResetAll,
  onRevertToSaved,
}: ResetFiltersMenuProps) => {
  // No current filters, but has unsaved changes (user cleared filters,
  // saved view has them) → show only "Revert to saved" button
  if (!hasCurrentFilters && hasUnsavedChanges) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="text-xs text-muted-foreground hover:text-foreground"
        onClick={onRevertToSaved}
      >
        Revert to saved
      </Button>
    )
  }

  // Has current filters, no unsaved changes (matches saved) → simple
  // clear button
  if (hasCurrentFilters && !hasUnsavedChanges) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="text-xs text-muted-foreground hover:text-foreground"
        onClick={onResetAll}
      >
        {label}
      </Button>
    )
  }

  // Has current filters AND unsaved changes → dropdown with both options
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {label}
          <ChevronsUpDown className="size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onResetAll}>Clear all</DropdownMenuItem>
        <DropdownMenuItem onClick={onRevertToSaved}>
          Revert to saved
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const MemoizedResetFiltersMenu = memo(ResetFiltersMenu)

MemoizedResetFiltersMenu.displayName = 'ResetFiltersMenu'

export { MemoizedResetFiltersMenu as ResetFiltersMenu }
